import { useState } from 'react';
import { useUnifiedFeed } from '@/hooks/useUnifiedFeed';
import { useFeedStore } from '@/store';
import { UnifiedContentCard } from '@/components/feed/UnifiedContentCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Loader2, 
  RefreshCw, 
  Filter,
  SlidersHorizontal,
  Image as ImageIcon,
  Newspaper,
  Twitter,
  Globe,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { cn, formatRelativeTime } from '@/utils';
import type { TweetFilter } from '@/types';

// Category options with icons
const categoryOptions = [
  { value: 'all', label: '全部', icon: null },
  { value: 'news', label: '新闻', icon: '📰' },
  { value: 'opinion', label: '观点', icon: '💭' },
  { value: 'analysis', label: '分析', icon: '📊' },
  { value: 'humor', label: '幽默', icon: '😄' },
  { value: 'discussion', label: '讨论', icon: '💬' },
  { value: 'technology', label: '科技', icon: '💻' },
  { value: 'business', label: '商业', icon: '💼' },
  { value: 'other', label: '其他', icon: '📝' },
];

// Sentiment options
const sentimentOptions = [
  { value: 'all', label: '全部情感' },
  { value: 'positive', label: '积极' },
  { value: 'negative', label: '消极' },
  { value: 'neutral', label: '中性' },
  { value: 'mixed', label: '复杂' },
];

// Content type options
const contentTypeOptions = [
  { value: 'all', label: '全部', icon: Globe },
  { value: 'tweets', label: '推文', icon: Twitter },
  { value: 'rss', label: 'RSS', icon: Newspaper },
];

export default function FeedPage() {
  const { filter, setFilter } = useFeedStore();
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [contentType, setContentType] = useState<'all' | 'tweets' | 'rss'>('all');
  
  const { data, isLoading, refetch, isRefetching } = useUnifiedFeed({
    category: filter.category === 'all' ? undefined : filter.category,
    content_type: contentType,
    time_window_hours: filter.timeWindow || 24,
    include_rss: true,
  });
  
  const items = data?.items || [];
  const contentBreakdown = data?.content_breakdown || { tweets: 0, rss: 0 };
  
  // Apply client-side filtering
  const filteredItems = items.filter(item => {
    if (filter.sentiment && filter.sentiment !== 'all') {
      if (item.enrichment?.sentiment !== filter.sentiment) return false;
    }
    if (filter.hasMedia && item.media_urls.length === 0) return false;
    return true;
  });
  
  const handleCategoryChange = (value: string) => {
    setFilter({ category: value as TweetFilter['category'] });
  };
  
  const handleRefresh = () => {
    refetch();
  };
  
  const toggleFilter = (filterType: string, value: string) => {
    const filterKey = `${filterType}:${value}`;
    if (activeFilters.includes(filterKey)) {
      setActiveFilters(prev => prev.filter(f => f !== filterKey));
      if (filterType === 'sentiment') setFilter({ sentiment: undefined });
      if (filterType === 'media') setFilter({ hasMedia: undefined });
    } else {
      setActiveFilters(prev => [...prev, filterKey]);
      if (filterType === 'sentiment') setFilter({ sentiment: value });
      if (filterType === 'media') setFilter({ hasMedia: true });
    }
  };
  
  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">信息流</h2>
          <p className="text-sm text-muted-foreground mt-1">
            实时追踪推文和RSS订阅
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Live indicator */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 rounded-full">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className="text-sm text-green-600 font-medium">实时</span>
          </div>
          
          {/* Refresh button */}
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefetching}
          >
            <RefreshCw className={cn("h-4 w-4", isRefetching && "animate-spin")} />
          </Button>
          
          {/* Filter toggle */}
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            className={cn(showFilters && "bg-muted")}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Content type selector */}
      <div className="flex items-center gap-2 mb-4">
        {contentTypeOptions.map((opt) => {
          const Icon = opt.icon;
          const count = opt.value === 'tweets' 
            ? contentBreakdown.tweets 
            : opt.value === 'rss' 
              ? contentBreakdown.rss 
              : contentBreakdown.tweets + contentBreakdown.rss;
          
          return (
            <Button
              key={opt.value}
              variant={contentType === opt.value ? 'default' : 'outline'}
              size="sm"
              className="h-9"
              onClick={() => setContentType(opt.value as typeof contentType)}
            >
              <Icon className="h-4 w-4 mr-1.5" />
              {opt.label}
              <Badge variant="secondary" className="ml-1.5 text-xs">
                {count}
              </Badge>
            </Button>
          );
        })}
      </div>
      
      {/* Category tabs */}
      <div className="mb-4 overflow-x-auto">
        <Tabs value={filter.category} onValueChange={handleCategoryChange}>
          <TabsList className="inline-flex h-10">
            {categoryOptions.map((cat) => (
              <TabsTrigger 
                key={cat.value} 
                value={cat.value}
                className="px-4"
              >
                {cat.icon && <span className="mr-1.5">{cat.icon}</span>}
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      
      {/* Advanced filters */}
      {showFilters && (
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="space-y-4">
              {/* Sentiment filter */}
              <div>
                <label className="text-sm font-medium mb-2 block">情感倾向</label>
                <div className="flex flex-wrap gap-2">
                  {sentimentOptions.map((opt) => (
                    <Badge
                      key={opt.value}
                      variant={filter.sentiment === opt.value ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleFilter('sentiment', opt.value)}
                    >
                      {opt.label}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Media filter */}
              <div>
                <label className="text-sm font-medium mb-2 block">内容类型</label>
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant={filter.hasMedia ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleFilter('media', 'true')}
                  >
                    <ImageIcon className="w-3 h-3 mr-1" />
                    含媒体
                  </Badge>
                  <Badge
                    variant={filter.isInformative ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setFilter({ isInformative: !filter.isInformative })}
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    高信息价值
                  </Badge>
                  <Badge
                    variant={filter.isControversial ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setFilter({ isControversial: !filter.isControversial })}
                  >
                    <AlertCircle className="w-3 h-3 mr-1" />
                    争议内容
                  </Badge>
                </div>
              </div>
              
              {/* Active filters summary */}
              {(filter.sentiment || filter.hasMedia) && (
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">已选筛选:</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFilter({ 
                          sentiment: undefined, 
                          hasMedia: undefined,
                          isInformative: undefined,
                          isControversial: undefined,
                        });
                        setActiveFilters([]);
                      }}
                    >
                      清除全部
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Stats bar */}
      <div className="flex items-center justify-between mb-4 text-sm text-muted-foreground">
        <span>
          显示 {filteredItems.length} 条内容
          {data?.total && data.total > filteredItems.length && ` (共 ${data.total} 条)`}
        </span>
        <span>
          最后更新: {formatRelativeTime(new Date().toISOString())}
        </span>
      </div>
      
      {/* Content items */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredItems.length > 0 ? (
        <div className="space-y-4">
          {filteredItems.map((item) => (
            <UnifiedContentCard 
              key={item.id} 
              item={item}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Filter className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium">暂无符合条件的内容</p>
            <p className="text-sm text-muted-foreground mt-2">
              尝试调整筛选条件或等待新数据同步
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => {
                setFilter({ 
                  category: 'all',
                  sentiment: undefined, 
                  hasMedia: undefined,
                });
                setContentType('all');
              }}
            >
              重置筛选
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Load more */}
      {data?.has_more && (
        <div className="flex justify-center mt-6">
          <Button variant="outline">加载更多</Button>
        </div>
      )}
    </div>
  );
}
